// src/pages/ProgressPage.jsx
import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from 'recharts';
import LoadingSpinner from '../components/LoadingSpinner';
import './ProgressPage.css';
import { Medal, Trophy, BookOpen, Coins } from 'lucide-react';
import { fetchStudentProgressMetrics } from '../api/studentApi';
import SectionHeader from '../components/SectionHeader';
import StreakCard from '../components/StreakCard';
import StudentHeaderV2 from '../components/StudentHeaderV2';

export default function ProgressPage() {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProgressMetrics = async () => {
            try {
                const data = await fetchStudentProgressMetrics();
                setMetrics(data.data.data);
                console.log("Fetched Progress Metrics:", data.data.data);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching progress metrics:", error);
                setLoading(false);
            }
        };

        fetchProgressMetrics();
    }, []);

    const formatDateLabel = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);

        // Uses built-in internationalization to get short month name (e.g., "Jul") + day number
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="progress-page-container">
            <StudentHeaderV2/>
            <main className="progress-main-layout">
                {loading ? (<div className="loading-box"><LoadingSpinner /></div>) : (
                    <>
                        <StreakCard streak={metrics?.streakData} className="streak-card" />
                        <br/>
                        {/* 📈 FEATURE 1: 10-Day Performance Timeline */}
                        <div className="analytics-charts-split-row">

                            {/* Left Chart: XP Cumulative Volume */}
                            <section className="progress-card dashboard-chart-box">
                                <SectionHeader icon={Coins} title="XP Points Earned" />
                                <div className="chart-container-frame">
                                    <ResponsiveContainer width="100%" height={220}>
                                        <AreaChart data={metrics?.graphData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="chartPoints" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.25} />
                                                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                            <XAxis dataKey="date" stroke="#94a3b8" style={{ fontSize: '0.75rem' }} tickFormatter={formatDateLabel} />
                                            <YAxis stroke="#94a3b8" style={{ fontSize: '0.75rem' }} />
                                            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                                            <Area type="monotone" dataKey="totalPoints" name="XP" stroke="#6366f1" strokeWidth={2.5} fillOpacity={1} fill="url(#chartPoints)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </section>

                            {/* Right Chart: Discrete Quiz Counts */}
                            <section className="progress-card dashboard-chart-box">
                                <SectionHeader icon={BookOpen} title="Quizzes Completed" />
                                <div className="chart-container-frame">
                                    <ResponsiveContainer width="100%" height={220}>
                                        {/* Using a BarChart here captures individual daily counts beautifully */}
                                        <BarChart data={metrics?.graphData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                            <XAxis dataKey="date" stroke="#94a3b8" style={{ fontSize: '0.75rem' }} tickFormatter={formatDateLabel} />
                                            <YAxis stroke="#94a3b8" style={{ fontSize: '0.75rem' }} allowDecimals={false} />
                                            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                                            <Bar dataKey="quizzesCount" name="Quizzes" fill="#10b981" radius={[4, 4, 0, 0]} barSize={24} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </section>
                        </div>
                        <div className="progress-split-dashboard-grid">
                            {/* ⚠️ FEATURE 4: Focus Areas */}
                            <section className="progress-card focus-zone">
                                <SectionHeader icon={Trophy} title="Attention Areas (< 70%)" />
                                <div className="focus-list">
                                    {metrics?.reviewTopics.length === 0 ? (
                                        <p className="clean-status-notice">✨ Great job! All your topic targets are performing above 70%.</p>
                                    ) : (
                                        metrics?.reviewTopics.map(item => (
                                            <div key={item.topic_id} className="focus-topic-row">
                                                <span>{item.Topic?.name}</span>
                                                <span className="low-score-indicator">{Math.round(item.avgScore)}%</span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </section>

                            {/* 🏅 FEATURE 3: Recent Badges (Max 10) */}
                            <section className="progress-card badges-snapshot-pane">
                                <SectionHeader icon={Medal} title="Recent Achievements" />
                                <div className="badges-mini-flex-strip">
                                    {metrics?.recentBadges.length === 0 ? (
                                        <p className="empty-subtext">Keep taking quizzes to unlock specialty badges!</p>
                                    ) : (
                                        metrics?.recentBadges.map(ub => {
                                            // 🌟 Parse "earnedAt" into a clean "July 5" human-readable format
                                            const formattedDate = ub.earnedAt
                                                ? new Date(ub.earnedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
                                                : '';

                                            return (
                                                <div key={ub.id} className="badge-mini-card" title={`Earned for ${ub.Topic?.name}`}>
                                                    <img src={ub.Badge?.imageUrl || '/placeholder-badge.png'} alt={ub.Badge?.name} />
                                                    <strong>{ub.Badge?.name}</strong>

                                                    {/* Displays the dynamic topic name */}
                                                    <div className="badge-topic-lbl">{ub.Topic?.name}</div>

                                                    {/* 🌟 New: Displays the formatted achievement date stamp */}
                                                    <div className="badge-date-lbl">{formattedDate}</div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </section>
                        </div>

                        {/* 📝 FEATURE 2: Recent Quizzes Snapshot (Max 20) */}
                        <section className="progress-card table-card">
                            <SectionHeader icon={BookOpen} title="Recent Quiz History" />
                            <div className="table-responsive-wrapper">
                                <table className="history-data-table">
                                    <thead>
                                        <tr>
                                            <th>Topic</th>
                                            <th>Points</th>
                                            <th>Accuracy</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {metrics?.recentQuizzes.map(quiz => {
                                            // 🌟 Calculate the accuracy percentage inline dynamically
                                            const total = quiz.totalQuestions || 0;
                                            const correct = quiz.correctAnswers || 0;
                                            const scorePercentage = total > 0 ? Math.round((correct / total) * 100) : 0;

                                            return (
                                                <tr key={quiz.id}>
                                                    <td className="table-topic-bold">{quiz.Topic?.name}</td>

                                                    {/* Updated to use creditsEarned mapping variable */}
                                                    <td className="xp-gain-column">+{quiz.creditsEarned || 0} XP</td>

                                                    {/* Displays the calculated score along with a fraction string for clarity */}
                                                    <td>
                                                        <span className="percentage-text">{scorePercentage}%</span>
                                                        <span className="fraction-subtext"> ({correct}/{total})</span>
                                                    </td>

                                                    <td>
                                                        <span className={`status-tag ${scorePercentage >= 70 ? 'passed' : 'needs-work'}`}>
                                                            {scorePercentage >= 70 ? 'Mastered' : 'Review'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </>)}

            </main>
        </div>
    );
}